import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';
import type { FileCategoryId, FragmentType } from '@e-socna/data-layer';

import { CheckboxList } from '../../components/CheckboxList';
import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import {
  PATH_RESOLVABLE_MISSION_TYPE_FILES,
  PATH_RESOLVABLE_MISSION_TYPE_FILES_EDIT,
} from '../../app/constants/paths.constant';
import { FormVariant } from '../../forms';
import {
  ResolvableMissionTypeForm,
  ResolvableMissionTypeFormTab,
} from '../../forms/ResolvableMissionType';
import {
  buildMissionFileCategoryTree,
  toMissionFileCategoryCheckboxListItems,
} from '../../forms/ResolvableMissionType/utils/mission-file-category-tree.util';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const ResolvableMissionTypePage_Files_ResolvableMissionType_Fragment =
  graphql(`
    fragment ResolvableMissionTypePage_Files_ResolvableMissionType_Fragment on ResolvableMissionType {
      _id
      allowedFileCategoryIds
      ...ResolvableMissionTypeForm_ResolvableMissionType_Fragment
    }
  `);

interface Props {
  readonly resolvableMissionType: FragmentType<
    typeof ResolvableMissionTypePage_Files_ResolvableMissionType_Fragment
  >;
}

export const Files = ({
  resolvableMissionType: pResolvableMissionType,
}: Readonly<Props>) => {
  const resolvableMissionType = getFragmentData(
    ResolvableMissionTypePage_Files_ResolvableMissionType_Fragment,
    pResolvableMissionType,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_RESOLVABLE_MISSION_TYPE_FILES,
      edit: PATH_RESOLVABLE_MISSION_TYPE_FILES_EDIT,
    },
    { id: resolvableMissionType._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { missionFileCategories } = useReferencesContext();

  const missionFileCategoryTree = useMemo(
    () =>
      buildMissionFileCategoryTree(
        missionFileCategories.filter(
          (fileCategory) => !fileCategory.hideInFileExplorer,
        ),
      ),
    [missionFileCategories],
  );

  const allowedFileCategoryIds =
    (resolvableMissionType.allowedFileCategoryIds ?? undefined) as
      readonly FileCategoryId[] | undefined;

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Paper sx={{ p: 2 }}>
          <Typography gutterBottom variant="h6">
            Catégories de fichiers autorisées
          </Typography>
          <CheckboxList<FileCategoryId>
            idPrefix="mission-type-file-categories-"
            items={toMissionFileCategoryCheckboxListItems(
              missionFileCategoryTree,
            )}
            value={allowedFileCategoryIds ?? EMPTY_ARRAY}
            variant="consultation"
          />
        </Paper>
      </Stack>
      <SpeedDial
        actions={[
          {
            id: 'editMissionTypeFilesParameters',
            icon: <EditIcon />,
            name: 'Modifier Paramétrage Fichiers',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur le Paramétrage des Fichiers du Type de Mission"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <ResolvableMissionTypeForm
          formVariant={FormVariant.Modification}
          initialValues={resolvableMissionType}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          resolvableMissionTypeId={resolvableMissionType._id}
          stepIdsSubset={[ResolvableMissionTypeFormTab.Files]}
          title="Formulaire de modification du Paramétrage des Fichiers du Type de Mission"
        />
      </RightDrawer>
    </>
  );
};
